/**
 * DATAFLOW-000001 — Founder System Explorer
 * Transparency only: ownership, counts, SoT. No business feature work.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  amazonConnectApi,
  type BusinessMetricsStatus,
  type DataflowEntityRow,
  type DataflowExplorer,
  type SellerConnection,
} from '../../api/amazonconnect';

function statusClass(s: string): string {
  if (s === 'PASS') return 'bg-emerald-100 text-emerald-900';
  if (s === 'FAIL') return 'bg-red-100 text-red-900';
  if (s === 'NOT_IMPLEMENTED') return 'bg-slate-200 text-slate-700';
  return 'bg-amber-100 text-amber-900';
}

export function SystemExplorerPage() {
  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [data, setData] = useState<DataflowExplorer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<DataflowEntityRow | null>(null);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [bmeStatus, setBmeStatus] = useState<BusinessMetricsStatus | null>(null);
  const [bmeBusy, setBmeBusy] = useState(false);
  const [bmeErr, setBmeErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setBmeErr(null);
    try {
      const conns = await amazonConnectApi.listConnections();
      setConnections(conns);
      const cid = connectionId || conns[0]?.id || '';
      if (!connectionId && conns[0]?.id) setConnectionId(conns[0].id);
      const exp = await amazonConnectApi.dataflowExplorer(cid || undefined);
      setData(exp);
      if (cid) {
        try {
          setBmeStatus(await amazonConnectApi.businessMetricsStatus(cid));
        } catch {
          setBmeStatus(null);
        }
      } else {
        setBmeStatus(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load System Explorer');
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  const recomputeBME = async () => {
    if (!connectionId) return;
    setBmeBusy(true);
    setBmeErr(null);
    try {
      await amazonConnectApi.recomputeBusinessMetrics(connectionId);
      await load();
    } catch (e) {
      setBmeErr(e instanceof Error ? e.message : 'BME recompute failed');
    } finally {
      setBmeBusy(false);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  const openEntity = async (row: DataflowEntityRow) => {
    setSelected(row);
    setDetail(null);
    setDetailErr(null);
    try {
      const d = await amazonConnectApi.dataflowEntityDetail(row.entity_id, connectionId || undefined);
      setDetail(d);
    } catch (e) {
      setDetailErr(e instanceof Error ? e.message : 'detail failed');
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <header className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          DATAFLOW-000001 · Founder Engineering
        </p>
        <h1 className="text-2xl font-semibold text-slate-900">System Explorer</h1>
        <p className="max-w-3xl text-sm text-slate-600">
          Where data originates, is stored, transformed, and displayed. No redesign — ownership and live
          counts only. Source:{' '}
          <code className="rounded bg-slate-100 px-1 text-xs">GET /api/v1/amazon/dataflow/explorer</code>
        </p>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to="/amazon/connect" className="text-brand-700 hover:underline">
            Amazon Connect
          </Link>
          <Link to="/amazon/discovery" className="text-brand-700 hover:underline">
            Amazon Discovery
          </Link>
          <Link to="/configuration" className="text-brand-700 hover:underline">
            Configuration
          </Link>
          <Link to="/docs/platform" className="pointer-events-none text-slate-400" aria-disabled>
            Docs: DATAFLOW-000001_*
          </Link>
        </div>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="text-slate-500">Connection filter</span>
          <select
            className="mt-1 block min-w-[16rem] rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            value={connectionId}
            onChange={(e) => setConnectionId(e.target.value)}
          >
            <option value="">All (tenant aggregate)</option>
            {connections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.seller_ref || c.id.slice(0, 8)} · {c.status}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-sm text-white"
        >
          Refresh
        </button>
        <button
          type="button"
          disabled={!connectionId || bmeBusy}
          onClick={() => void recomputeBME()}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 disabled:opacity-50"
          title="Recompute Business Metrics daily snapshots from MarketSync feeds"
        >
          {bmeBusy ? 'Recomputing BME…' : 'Recompute Business Metrics'}
        </button>
      </div>

      {bmeErr && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{bmeErr}</p>
      )}

      {bmeStatus && (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-900">Business Metrics Engine</h2>
          <p className="mt-1 text-xs text-slate-500">
            Sole owner of derived commerce metrics · Group A Amazon · Group B Calculated/Derived
          </p>
          <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-slate-500">Snapshot date</dt>
              <dd className="font-mono text-slate-900">{bmeStatus.snapshot_date ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Daily records</dt>
              <dd className="font-mono text-slate-900">{bmeStatus.records ?? 0}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Last run</dt>
              <dd className="font-mono text-slate-900">{bmeStatus.last_run_status ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Calculation time</dt>
              <dd className="font-mono text-slate-900">
                {bmeStatus.calculation_time_ms != null ? `${bmeStatus.calculation_time_ms} ms` : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Rows processed / updated / skipped</dt>
              <dd className="font-mono text-slate-900">
                {bmeStatus.rows_processed ?? 0} / {bmeStatus.rows_updated ?? 0} /{' '}
                {bmeStatus.rows_skipped ?? 0}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Errors / warnings</dt>
              <dd className="font-mono text-slate-900">
                {bmeStatus.errors ?? 0} / {bmeStatus.warnings ?? 0}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-slate-500">Source</dt>
              <dd className="text-slate-800">{bmeStatus.source ?? '—'}</dd>
            </div>
          </dl>
        </section>
      )}

      {loading && <p className="text-sm text-slate-500">Loading ownership map…</p>}
      {error && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}

      {data && (
        <>
          <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h2 className="text-sm font-semibold text-slate-900">Founder Engineering Panel</h2>
            <dl className="mt-2 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-3">
              {Object.entries(data.engineering).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="font-mono text-slate-800">{String(v)}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-2 text-[11px] text-slate-500">
              API: GET /api/v1/amazon/dataflow/explorer · DB ownership tables listed below · Storage:{' '}
              {data.runtime.storage_mode}
            </p>
          </section>

          <section className="rounded-lg border border-slate-200 p-4">
            <h2 className="text-sm font-semibold">Runtime observability</h2>
            <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <li>MarketSync: {data.runtime.marketsync_bound ? 'bound' : 'NOT BOUND'}</li>
              <li>Queue/active-ish: {data.runtime.queue_jobs_active_or_queued}</li>
              <li>DLQ: {data.runtime.dlq_count}</li>
              <li>Workers: {data.runtime.workers_configured}</li>
            </ul>
            {data.runtime.last_error_summary && (
              <p className="mt-2 text-xs text-red-700">Last DLQ: {data.runtime.last_error_summary}</p>
            )}
          </section>

          <section className="rounded-lg border border-slate-200 p-4">
            <h2 className="text-sm font-semibold">Certification verdicts</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(data.verdicts).map(([k, v]) => (
                <span key={k} className={`rounded px-2 py-1 text-xs font-medium ${statusClass(v)}`}>
                  {k}: {v}
                </span>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold">Entities</h2>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="px-2 py-2">Entity</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Amazon</th>
                    <th className="px-2 py-2">Raw</th>
                    <th className="px-2 py-2">Canonical</th>
                    <th className="px-2 py-2">DB</th>
                    <th className="px-2 py-2">API total</th>
                    <th className="px-2 py-2">Displayed</th>
                    <th className="px-2 py-2">Remaining</th>
                    <th className="px-2 py-2">Mismatch</th>
                    <th className="px-2 py-2">API</th>
                    <th className="px-2 py-2">Last sync</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entities.map((row) => (
                    <tr
                      key={row.entity_id}
                      className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                      onClick={() => void openEntity(row)}
                    >
                      <td className="px-2 py-2 font-medium text-slate-900">{row.entity}</td>
                      <td className="px-2 py-2">
                        <span className={`rounded px-1.5 py-0.5 ${statusClass(row.status)}`}>{row.status}</span>
                      </td>
                      <td className="px-2 py-2 font-mono">{row.amazon_records ?? '—'}</td>
                      <td className="px-2 py-2 font-mono">{row.raw_records}</td>
                      <td className="px-2 py-2 font-mono">{row.canonical_records}</td>
                      <td className="px-2 py-2 font-mono">{row.database_records}</td>
                      <td className="px-2 py-2 font-mono">{row.api_records ?? '—'}</td>
                      <td className="px-2 py-2 font-mono">{row.displayed_records ?? '—'}</td>
                      <td className="px-2 py-2 font-mono">{row.remaining_records ?? '—'}</td>
                      <td className="max-w-[14rem] truncate px-2 py-2 text-[11px]" title={row.mismatch_reason || row.notes}>
                        {row.mismatch_reason || row.notes || '—'}
                      </td>
                      <td className="max-w-[10rem] truncate px-2 py-2 font-mono" title={row.rest_api}>
                        {row.rest_api}
                      </td>
                      <td className="px-2 py-2">
                        {row.last_sync_at ? new Date(row.last_sync_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">Click a row for endpoint / raw / canonical drilldown.</p>
          </section>

          {selected && (
            <section className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-sm font-semibold text-amber-950">Drilldown · {selected.entity}</h2>
                <button type="button" className="text-xs text-slate-600" onClick={() => setSelected(null)}>
                  Close
                </button>
              </div>
              <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                <div>
                  <dt className="text-slate-500">Amazon endpoint</dt>
                  <dd className="font-mono">{selected.amazon_endpoint}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Adapter</dt>
                  <dd>{selected.adapter}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Parser</dt>
                  <dd className="font-mono">{selected.parser}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">SoT</dt>
                  <dd>{selected.source_of_truth}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Screen</dt>
                  <dd>{selected.displayed_screen}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Component</dt>
                  <dd className="font-mono">{selected.frontend_component}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Job / request / correlation</dt>
                  <dd className="font-mono">
                    {selected.last_job_id || '—'} · {selected.last_request_id || '—'} ·{' '}
                    {selected.last_correlation_id || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Error</dt>
                  <dd className="text-red-800">{selected.last_error || selected.notes || 'none'}</dd>
                </div>
              </dl>
              {detailErr && <p className="mt-2 text-xs text-red-700">{detailErr}</p>}
              {detail && (
                <pre className="mt-3 max-h-64 overflow-auto rounded bg-slate-900 p-3 text-[10px] text-slate-100">
                  {JSON.stringify(detail, null, 2)}
                </pre>
              )}
            </section>
          )}

          <section className="rounded-lg border border-slate-200 p-4">
            <h2 className="text-sm font-semibold">Database ownership</h2>
            <div className="mt-2 overflow-x-auto">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-2 py-1">Table</th>
                    <th className="px-2 py-1">Purpose</th>
                    <th className="px-2 py-1">Owner</th>
                    <th className="px-2 py-1">Writes</th>
                    <th className="px-2 py-1">Reads</th>
                    <th className="px-2 py-1">Storage</th>
                  </tr>
                </thead>
                <tbody>
                  {data.database_ownership.map((t) => (
                    <tr key={t.table_name} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono">{t.table_name}</td>
                      <td className="px-2 py-1">{t.purpose}</td>
                      <td className="px-2 py-1">{t.owner}</td>
                      <td className="px-2 py-1">{t.who_writes}</td>
                      <td className="px-2 py-1">{t.who_reads}</td>
                      <td className="px-2 py-1">{t.storage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 p-4 text-xs text-slate-600">
            <h2 className="text-sm font-semibold text-slate-900">Unknown paths / SoT risks</h2>
            <ul className="mt-2 list-disc pl-5">
              {data.unknown_data_paths.map((x) => (
                <li key={x}>{x}</li>
              ))}
              {data.sot_violations.map((x) => (
                <li key={x} className="text-amber-800">
                  {x}
                </li>
              ))}
            </ul>
            <p className="mt-2">{data.trace_hint}</p>
          </section>
        </>
      )}
    </div>
  );
}
